import { faCoin } from "@fortawesome/pro-regular-svg-icons";

import { ChangeEvent, FunctionComponent, useCallback, useMemo, useState } from "react";

import { useTranslation } from "react-i18next";

import { zodResolver } from "@hookform/resolvers/zod";

import { useForm } from "react-hook-form";

import { Icon, Modal } from "@/components";
import { useHasPermissions } from "@/utils/useHasPermissions";
import { ERROR_TYPE, getAllErrors, renderErrorMessages } from "@/utils";
import { AiApplyType, CustomColumnFrequency, CustomColumnTypes } from "@/enum/list.enum";
import { PermissionRoles } from "@/enum";
import { InputField } from "@/components/InputField/InputField";
import { Dropdown, AutoCompleteItem } from "@/components/Dropdown/Dropdown";
import { profileTrackerSelector } from "@/pages/Private/redux/profileTracker/profileTracker.slice";
import { useAppSelector } from "@/redux/hooks";
import { useAddColumnToTrackerMutation } from "@/pages/Private/redux/profileTracker/profileTracker.api";

import { Radio } from "@/components/Radio/Radio";

import { ColumnConfig } from "@/pages/Private/pages/List/schema/columnConfig";

import { getCurrentPrices } from "@/pages/Private/pages/List/utils/currentPrices";

import { CustomColumn, CustomColumnSchema } from "../../../schema/trackerColumn";
import { Tracker } from "../../../schema/profileTracker";

export interface JobOfferModalProps {
	setShowConfirm: (value: boolean) => void;
	open: boolean;
	trackerId: number;
	setOpen: (value: boolean) => void;
	columnId?: number;
	column?: ColumnConfig;
	customColumnName?: string;
	selectedTrackerOrganizationIds?: number[];
	tracker?: Tracker;
}

export const JobOfferModal: FunctionComponent<JobOfferModalProps> = ({
	setShowConfirm,
	trackerId,
	open,
	setOpen,
	columnId,
	column,
	customColumnName,
	selectedTrackerOrganizationIds,
	tracker,
}) => {
	const { hasPermissions } = useHasPermissions();
	const [applyTo, setApplyTo] = useState<AiApplyType>(AiApplyType.NONE);
	const [frequency, setFrequency] = useState<CustomColumnFrequency>();

	const [updateOrOnce, setUpdateOrOnce] = useState<boolean>(false);
	const { t } = useTranslation();
	const ts = useCallback((key: string) => t(`aiColumn.${key}`), [t]);
	const [sumUpScoreOverTime, setSumUpScoreOverTime] = useState<boolean>(false);

	const [update, { isLoading, error }] = useAddColumnToTrackerMutation();
	const { records } = useAppSelector(profileTrackerSelector);

	const {
		handleSubmit,
		getValues,
		setValue,
		formState: { errors },
	} = useForm<CustomColumn>({
		defaultValues: {
			customColumnType: CustomColumnTypes.JOB_OFFER,
			...column,
			customColumnName: customColumnName || column?.name,
			value: column?.config,
		},
		resolver: zodResolver(CustomColumnSchema),
	});

	console.log(errors);

	const trackerOrganizationIds =
		applyTo === AiApplyType.FIRST_10
			? records.slice(0, 10).map((p) => p.id)
			: applyTo === AiApplyType.CURRENTLY_SELECTED
				? selectedTrackerOrganizationIds
				: undefined;

	const onSubmit = async (values: CustomColumn) => {
		try {
			await update({
				...values,
				id: trackerId,
				updateOrOnce,
				frequency,
				sumUpScoreOverTime,
				columnId,
				trackerOrganizationIds,
			}).unwrap();

			setOpen(false);
		} catch (err) {
			console.error(err);
		}
	};

	const formErrors = Object.values(errors).map((error) => error?.message) as ERROR_TYPE[];

	const { recordsMeta } = useAppSelector(profileTrackerSelector);

	const currentCreditCost = useMemo(() => {
		return getCurrentPrices({
			applyTo,
			meta: recordsMeta,
			singlePrice: 1,
			selectedProspectIds: selectedTrackerOrganizationIds,
			tracker: tracker,
		});
	}, [applyTo, getValues("online"), recordsMeta.itemCount]);

	return (
		<Modal
			handleClose={() => {
				setShowConfirm(true);
			}}
			handleSave={handleSubmit(onSubmit)}
			isLoading={isLoading}
			isOpened={open}
			size="md"
			submitButtonText={ts("applyToAll")}
			title="Create Job offer Column"
		>
			<div className="text-ssm font-normal text-xs text-gray-700 mb-2">
				We search for jobs that have been posted in the last 30 days.
			</div>
			<div className="text-ssm font-medium mb-2">
				<InputField
					handleChange={function (event: ChangeEvent<HTMLInputElement>): void {
						setValue("customColumnName", event.target.value, {
							shouldValidate: true,
							shouldDirty: true,
						});
					}}
					label="Column Name"
					name={"name"}
					placeholder="Enter column name"
					value={getValues("customColumnName")}
				/>
			</div>

			<div className="text-ssm font-medium mb-2">
				<InputField
					handleChange={function (event: ChangeEvent<HTMLInputElement>): void {
						setValue("value", event.target.value, {
							shouldValidate: true,
							shouldDirty: true,
						});
					}}
					label="Job Title"
					name={"value"}
					placeholder="Enter job title"
					value={getValues("value")}
				/>
			</div>
			<div className="text-ssm font-normal text-xs text-gray-700 mb-2">
				Leave empty if you want to get the last job offer
			</div>

			<div className="flex flex-col mt-5 border-t pt-5 border-gray-200">
				<div className="flex  gap-4 flex-col text-gray-700 items-center">
					<Dropdown
						data={[
							{
								title: ts(CustomColumnFrequency.ONCE),
								id: CustomColumnFrequency.ONCE,
							},
							{
								title: ts(CustomColumnFrequency.DAILY),
								id: CustomColumnFrequency.DAILY,
							},
							{
								title: ts(CustomColumnFrequency.WEEKLY),
								id: CustomColumnFrequency.WEEKLY,
							},
							{
								title: ts(CustomColumnFrequency.MONTHLY),
								id: CustomColumnFrequency.MONTHLY,
							},
							{
								title: ts(CustomColumnFrequency.QUATERLY),
								id: CustomColumnFrequency.QUATERLY,
							},
							{
								title: ts(CustomColumnFrequency.HALFYEARLY),
								id: CustomColumnFrequency.HALFYEARLY,
							},
							{
								title: ts(CustomColumnFrequency.YEARLY),
								id: CustomColumnFrequency.YEARLY,
							},
						]}
						defaultValue={
							getValues("frequency") && {
								title: ts(getValues("frequency") as CustomColumnFrequency),
								id: getValues("frequency") as CustomColumnFrequency,
							}
						}
						error={!!errors.frequency?.message}
						floating={true}
						handleSelect={function (value?: AutoCompleteItem) {
							if (value?.id) {
								setValue("frequency", value.id as CustomColumnFrequency);
								setFrequency(value.id as CustomColumnFrequency);
							}
						}}
						label="Execution frequency"
					/>
				</div>
				<div className="flex flex-col grow mr-2">
					<div className="text-ssm font-medium mt-4 mb-2">Update frequency</div>
					<div className="flex flex-col gap-4">
						<Radio
							className="whitespace-nowrap"
							isChecked={updateOrOnce}
							onChange={() => {
								setUpdateOrOnce(!updateOrOnce);
							}}
						>
							{ts("updateOnEachExecution")}
						</Radio>

						<Radio
							className="whitespace-nowrap"
							isChecked={!updateOrOnce}
							onChange={() => {
								setUpdateOrOnce(!updateOrOnce);
							}}
						>
							{ts("onceAfterFindingAValue")}
						</Radio>
					</div>
				</div>
			</div>
			<div className="flex flex-col mt-5 border-t pt-5 border-gray-200">
				<div className="flex flex-col grow mr-2">
					<div className="text-ssm font-medium mb-2">Scoring</div>
					<div className="flex flex-col gap-4">
						<InputField
							handleChange={function (event: ChangeEvent<HTMLInputElement>): void {
								setValue("score", +event.target.value, {
									shouldValidate: true,
									shouldDirty: true,
								});
							}}
							name={"score"}
							placeholder="Enter score"
							type="number"
							value={getValues("score")?.toString() || ""}
						/>
					</div>
				</div>
				<div className="flex flex-col grow mr-2">
					<div className="text-ssm font-medium mt-4 mb-2">Sum up score over time</div>
					<div className="flex flex-col gap-4">
						<Radio
							className="whitespace-nowrap"
							isChecked={sumUpScoreOverTime}
							onChange={() => {
								setSumUpScoreOverTime(!sumUpScoreOverTime);
							}}
						>
							{ts("sumUpScoreOverTime")}
						</Radio>

						<Radio
							className="whitespace-nowrap"
							isChecked={!sumUpScoreOverTime}
							onChange={() => {
								setSumUpScoreOverTime(!sumUpScoreOverTime);
							}}
						>
							{ts("doNotSumUpScoreOverTime")}
						</Radio>
					</div>
				</div>
			</div>
			<div className="flex flex-row mt-5 border-t pt-5 border-gray-200">
				<Dropdown
					data={[
						{
							title: ts(AiApplyType.NONE),
							id: AiApplyType.NONE,
						},
						{
							title: ts(AiApplyType.FIRST_10),
							id: AiApplyType.FIRST_10,
						},
						{
							title: ts(AiApplyType.CURRENTLY_FILTERED),
							id: AiApplyType.CURRENTLY_FILTERED,
						},
						{
							title: ts(AiApplyType.CURRENTLY_SELECTED),
							id: AiApplyType.CURRENTLY_SELECTED,
						},
						{
							title: ts(AiApplyType.ALL),
							id: AiApplyType.ALL,
						},
						{
							title: ts(AiApplyType.ALL_INCLUDING_ASSIGNED),
							id: AiApplyType.ALL_INCLUDING_ASSIGNED,
						},
					]}
					floating={false}
					handleSelect={function (value?: AutoCompleteItem) {
						if (value?.id) {
							setApplyTo(value.id as AiApplyType);
						}
					}}
					label="Apply to ..."
					value={applyTo ? { title: ts(applyTo), id: applyTo } : undefined}
				/>
			</div>
			{hasPermissions([PermissionRoles.GLOBAL_ADMIN]) && (
				<>
					<div className="flex flex-row mt-5 border-t pt-5 border-gray-200 text-gray-700 items-center text-sm">
						<span className="mr-1">1x</span>
						<Icon className="w-[16px] h-[16px] text-gray-700 mr-1" icon={faCoin} />
						<span className="">per row</span>
					</div>
					{applyTo && currentCreditCost > 0 && (
						<div className="flex flex-row pt-5 text-gray-700 items-center text-sm">
							<span className="mr-1">{currentCreditCost}</span>
							<Icon className="w-[16px] h-[16px] text-gray-700 mr-1" icon={faCoin} />
							<span className="">total</span>
						</div>
					)}
				</>
			)}
			{getAllErrors(error, formErrors).length ? (
				<div className="mt-4 mb-0">{renderErrorMessages(getAllErrors(error, formErrors))}</div>
			) : null}
		</Modal>
	);
};
